import { Table as TableIcon } from "@untitledui/icons";
import { useMemo } from "react";
import { Focusable } from "react-aria-components";
import { UnboxTable } from "@/components/application/table/unbox-table";
import type { UnboxTableColumn, UnboxTableProps } from "@/components/application/table/unbox-table.types";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { useCurrency } from "@/hooks/use-currency";
import type { useExplorerDateFilter } from "@/hooks/use-explorer-date-filter";
import { TrendBadge } from "@/pages/explorers/utils";
import type { ProductRow } from "./product-explorer";

const videoFormatter = new Intl.NumberFormat("en-US", {
  compactDisplay: "short",
});

export interface ProductExplorerTableProps
  extends Omit<
    UnboxTableProps<ProductRow>,
    "columns" | "rowId" | "emptyState" | "filteredEmptyState" | "size" | "rounded" | "className" | "aria-label"
  > {
  dateRange: ReturnType<typeof useExplorerDateFilter>["dateRange"];
  onClearFilters: () => void;
}

export function ProductExplorerTable({
  rows,
  dateRange,
  onClickRow,
  sorting,
  pagination,
  hasActiveFilters,
  onClearFilters,
}: ProductExplorerTableProps) {
  const currency = useCurrency();
  const gmvFormatter = useMemo(
    () => new Intl.NumberFormat("en-US", { style: "currency", currency, notation: "compact", compactDisplay: "short" }),
    [currency],
  );

  const columns = useMemo<UnboxTableColumn<ProductRow>[]>(
    () => [
      {
        id: "productTitle",
        label: "Product",
        sortable: true,
        minWidth: "400px",
        render: (_value, row) => (
          <div className="flex items-center gap-3 min-w-0">
            {row.thumbnail ? (
              <img
                src={row.thumbnail}
                alt={row.productName || "Product"}
                className="h-10 w-10 rounded object-cover flex-shrink-0"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-gray-100 flex-shrink-0">
                <span className="text-xs font-medium text-quaternary">
                  {row.productName?.charAt(0).toUpperCase() ?? "?"}
                </span>
              </div>
            )}
            <span className="inline-flex flex-1 min-w-0 justify-start contain-inline-size">
              <Tooltip title={row.productName}>
                <Focusable>
                  <p className="truncate min-w-0 text-sm font-medium text-primary">{row.productName}</p>
                </Focusable>
              </Tooltip>
            </span>
          </div>
        ),
      },
      {
        id: "videos",
        label: "Videos",
        sortable: true,
        minWidth: "110px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center flex-wrap">
            {videoFormatter.format(row.videos)}
            <TrendBadge change={row.videosChange} dateRange={dateRange} />
          </div>
        ),
      },
      {
        id: "gmv",
        label: "GMV (Last Week)",
        sortable: true,
        minWidth: "140px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center flex-wrap">
            {gmvFormatter.format(row.gmv ?? 0)}
            <TrendBadge change={row.gmvChange} dateRange={dateRange} />
          </div>
        ),
      },
      {
        id: "unitSales",
        label: "Unit Sales",
        sortable: true,
        minWidth: "110px",
        render: (_value, row) => (
          <div className="flex flex-row gap-1 items-center flex-wrap">
            {videoFormatter.format(row.unitSales ?? 0)}
            <TrendBadge change={row.unitSalesChange} dateRange={dateRange} />
          </div>
        ),
      },
      {
        id: "price",
        label: "Price",
        sortable: true,
        minWidth: "100px",
        render: (_value, row) => {
          const formatted = row.price.length
            ? row.price.map((price) => gmvFormatter.format(price)).join(", ")
            : "No Prices";
          return (
            <div className="flex flex-row gap-1 items-center whitespace-nowrap" title={formatted}>
              {formatted}
            </div>
          );
        },
      },
      {
        id: "stockCount",
        label: "Stock",
        sortable: true,
        rowAccessor: "stockCount",
        minWidth: "80px",
      },
    ],
    [gmvFormatter, dateRange],
  );

  return (
    <UnboxTable<ProductRow>
      aria-label="Product Explorer"
      className="max-h-[60vh]"
      columns={columns}
      rows={rows}
      rowId="productId"
      onClickRow={onClickRow}
      sorting={sorting}
      pagination={pagination}
      hasActiveFilters={hasActiveFilters}
      emptyState={{ icon: TableIcon, title: "Nothing to see here yet" }}
      filteredEmptyState={{ onClearFilters }}
    />
  );
}
